// Browser-safe catalog of the Beacon Research machine-spend gig: id, label,
// blurb, the data-entry field spec, and the conformance teaching hint.
//
// Mirrors gig-catalog.ts (Torbridge) and gig-catalog-clinical.ts (Veralta) in
// shape and role, for the single gig surfaced in the Beacon Research app. It
// imports NOTHING heavy — no profile/schema JSON, no seal path, no Node
// built-ins — so both the browser UI and the /api/run Function can import it.
// It is the single source of truth for the gig id and input shape:
//   - the UI renders the purchase data-entry form from `fields`,
//   - the Beacon /api/run validates the submitted inputs against `fields`,
//   - src/live/gigs-spend.ts (the seal-bound gig) reuses this id.
// A test asserts every spend catalog id resolves to a gig so the two cannot
// drift.
//
// The seal-side gig reads: resource, category, amountUsd, purchaseId, taskRef.
// `category` is a closed select — the first three are on the mandate's
// allowlist; the last is the off-allowlist violation lever. The per-purchase
// cap is $500 (SPEND_PER_PURCHASE_CAP in gigs-spend.ts); an amount over it is
// the other violation lever.

import type { GigCatalogEntry } from "./gig-catalog.js";

export const SPEND_GIG_CATALOG: GigCatalogEntry[] = [
  {
    id: "resource_purchase",
    label: "Autonomous research: buy a paid resource",
    blurb:
      "Beacon's research agent buys a paid resource it needs to finish a task (a search-API call, a licensed-data pull, or a block of compute) under a bounded spend mandate. The purchase is sealed into a receipt bound to a per-purchase cap and a set of allowed categories. Enter a purchase and run it: the agent seals a real Sequesign receipt server-side, then you verify it here in your browser.",
    usesReview: false,
    runLabel: "Seal the purchase",
    fields: [
      {
        key: "amountUsd",
        label: "Purchase amount (USD)",
        type: "number",
        default: "320",
        min: 0,
        help: "Per-purchase cap is $500. Enter more than $500 to seal a receipt that records conformant: false."
      },
      {
        // A closed set (no free text): the first three are on the mandate's
        // allowed-category allowlist; "consulting" is off-allowlist and is the
        // second violation lever. Keep in sync with SPEND_ALLOWED_CATEGORIES in
        // gigs-spend.ts (the catalog stays free of the seal path).
        key: "category",
        label: "Category",
        type: "select",
        default: "licensed_data",
        options: ["search_api", "licensed_data", "compute", "consulting"],
        help: "Allowed: search_api, licensed_data, compute. “consulting” is off the allowlist."
      },
      {
        key: "resource",
        label: "Resource",
        type: "text",
        default: "Cardinal Markets: market dataset pull",
        help: "What was bought. A label only; the amount and category are what the mandate binds."
      },
      {
        key: "purchaseId",
        label: "Purchase id",
        type: "text",
        default: "buy-2026-0814-03"
      },
      {
        key: "taskRef",
        label: "Task reference",
        type: "text",
        default: "q3-competitive-landscape",
        help: "An opaque id for the task this purchase served."
      }
    ],
    violationHint:
      "Set the amount over $500, or pick the off-allowlist category “consulting”, to seal a receipt that honestly records conformant: false."
  }
];

export function spendCatalogEntry(id: string | undefined): GigCatalogEntry | undefined {
  if (id === undefined) return SPEND_GIG_CATALOG[0];
  return SPEND_GIG_CATALOG.find((g) => g.id === id);
}
